import React, { useEffect, useState, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { collection, getDocs } from "firebase/firestore";
import { useFirestore } from "../firebase/FirestoreContext";
import { EnvContext } from "../context/EnvContext";
import config from "../config";

const FeaturedCarousel = ({ featuredCollection, containerStyles }) => {
  const environment = useContext(EnvContext);
  const db = useFirestore();
  const collectionPath = environment === "staging" ? config.environment.staging.collectionPath : config.environment.production.collectionPath;

  const [currentIndex, setCurrentIndex] = useState(0);
  const slideInterval = useRef(null);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const isAutoScrolling = useRef(false); // Flag to distinguish auto-scroll from user interaction
  const [featuredProducts, setFeaturedProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasContent, setHasContent] = useState(true);
  const [carouselReady, setCarouselReady] = useState(false); // New state to track carousel readiness

  // Feature products
  useEffect(() => {
    const fetchFeaturedProducts = async () => {
      try {
        setLoading(true);
        const featuredCollectionRef = collection(db, `${collectionPath}Listings/FeaturedList/DataBase`);
        const featuredSnapshot = await getDocs(featuredCollectionRef);

        const featuredData = featuredSnapshot.docs
          .map((doc) => {
            const productData = doc.data();
            return {
              id: doc.id,
              featureCollection_name: productData.featureCollection_name || "N/A",
              sorting: productData.sorting || 0,
              ...productData,
            };
          })
          .filter((product) => product.featureCollection_name === featuredCollection && product.enabled === true)

          .sort((a, b) => a.sorting - b.sorting);

        setFeaturedProducts(featuredData);
        setHasContent(featuredData.length > 0); // Check if there are items
      } catch (error) {
        console.error("Error fetching featured products:", error);
        setHasContent(false);
      } finally {
        setLoading(false);
      }
    };

    fetchFeaturedProducts();
  }, [db, collectionPath, featuredCollection]);

  // Clone the list multiple times for smoother infinite scroll
  const repeatedProducts = featuredProducts.length > 1 ? Array(20).fill(featuredProducts).flat() : featuredProducts;

  // Start auto-slide
  const startAutoSlide = () => {
    if (featuredProducts.length <= 1) return; // No auto-slide if there's 0 or 1 product

    slideInterval.current = setInterval(() => {
      isAutoScrolling.current = true;
      setCurrentIndex((prevIndex) => prevIndex + 1);
    }, 5000);
  };

  // Stop and restart auto-slide
  const resetAutoSlide = () => {
    if (slideInterval.current) clearInterval(slideInterval.current);
    startAutoSlide();
  };

  // Auto-slide on mount
  useEffect(() => {
    if (featuredProducts.length > 1) {
      startAutoSlide();
    }
    return () => clearInterval(slideInterval.current); // Cleanup
  }, [featuredProducts]);

  // Scroll to the current index
  useEffect(() => {
    if (featuredProducts.length === 0 || !containerRef.current || !containerRef.current.firstChild) {
      console.warn("No products or container is not ready. Skipping scrollTo.");
      return;
    }

    const cardWidth = containerRef.current.firstChild.offsetWidth || 0;

    containerRef.current.scrollTo({
      left: currentIndex * cardWidth,
      behavior: isAutoScrolling.current ? "smooth" : "instant",
    });

    const timer = setTimeout(() => {
      isAutoScrolling.current = false; // Reset auto-scroll flag
      setCarouselReady(true); // Set carousel as ready after initial scroll
    }, 500);

    return () => clearTimeout(timer);
  }, [currentIndex, featuredProducts]);

  // Handle manual scrolling
  useEffect(() => {
    const handleScroll = () => {
      if (isAutoScrolling.current || featuredProducts.length === 0) return;

      if (containerRef?.current) {
        const cardWidth = containerRef?.current?.firstChild?.offsetWidth || 0;
        const newIndex = Math.round(containerRef?.current?.scrollLeft / cardWidth);

        setCurrentIndex(newIndex); // Update index based on scroll position
        resetAutoSlide();
      }
    };

    const container = containerRef?.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
    }

    return () => container?.removeEventListener("scroll", handleScroll);
  }, [featuredProducts]);

  // Reset to the middle of the repeated list
  useEffect(() => {
    if (featuredProducts.length === 0 || !containerRef.current || !containerRef.current.firstChild) {
      return;
    }

    const middleIndex = Math.floor(repeatedProducts.length / 2);
    if (currentIndex === 0 || currentIndex === repeatedProducts.length - 1) {
      setTimeout(() => {
        const firstChild = containerRef?.current?.firstChild;
        if (!firstChild) {
          return;
        }
        const cardWidth = firstChild.offsetWidth || 0;
        containerRef.current.scrollTo({
          left: middleIndex * cardWidth,
          behavior: "instant",
        });
        setCurrentIndex(middleIndex);
      }, 500); // Reduced the timeout to 500ms
    }
  }, [currentIndex, repeatedProducts.length, featuredProducts]);

  const handleDotClick = (index) => {
    if (featuredProducts.length === 0) return;

    const middleIndex = Math.floor(repeatedProducts.length / 2);
    setCurrentIndex(middleIndex + index); // Adjust for middle of repeated list
    resetAutoSlide();
  };

  if (loading) {
    return <div style={{ textAlign: "center", marginTop: "20px", height: 180, background: "#f5f5f5" }}></div>;
  }

  if (!hasContent) {
    return <div style={{ textAlign: "center", marginTop: "20px" }}></div>;
  }

  return (
    <div style={{ display: window.innerWidth <= 768 ? "block" : "none", ...containerStyles }}>
      <div style={styles.title}></div>
      <div
        ref={containerRef}
        style={{
          ...styles.carouselContainer,
          padding: featuredProducts.length === 1 ? "0px 10px" : "0",
          visibility: carouselReady ? "visible" : "hidden", // Hide carousel initially
        }}
      >
        {repeatedProducts.map((product, index) => (
          <div
            key={index}
            onClick={() => {
              if (product?.banner_target_url?.startsWith("http://") || product?.banner_target_url?.startsWith("https://")) {
                window.location.href = product.banner_target_url;
              } else {
                navigate(product.banner_target_url);
              }
            }}
            style={{
              ...styles.card,
              flex: featuredProducts.length === 1 ? "0 0 100%" : "0 0 100%",
              maxWidth: featuredProducts.length === 1 ? "100%" : "calc(100% - 20px)", // Set maxWidth to 100% for single banner
            }}
          >
            <img loading="lazy" src={product.image_url || "https://placehold.co/800x450"} alt={product.title} style={{ ...styles.image }} />
            <div style={styles.loaderOverlay}></div>
          </div>
        ))}
      </div>

      {featuredProducts.length > 1 && (
        <div style={styles.dotsContainer}>
          {featuredProducts.map((_, index) => {
            const isActive = currentIndex % featuredProducts.length === index;

            return (
              <div
                key={index}
                onClick={() => handleDotClick(index)}
                style={{
                  ...styles.dot,
                  backgroundColor: isActive ? "black" : "#b2b2b2",
                  opacity: isActive ? 1 : 0.5,
                }}
              ></div>
            );
          })}
        </div>
      )}
    </div>
  );
}; // End of return

export default FeaturedCarousel;

const styles = {
  title: {
    fontSize: 12,
    fontWeight: 600,
  },
  carouselContainer: {
    display: "flex",
    overflowX: "auto",
    scrollBehavior: "smooth",
    scrollSnapType: "x mandatory",
    gap: "10px",
    maxWidth: "100%",
    scrollbarWidth: "none",
  },
  card: {
    scrollSnapAlign: "center",
    position: "relative",
    overflow: "hidden",
    borderRadius: "12px",
    border: "1px solid #e0e0e0",
    background: "#fafafa",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    boxSizing: `border-box`, // Include padding in the card's total width
  },
  image: {
    width: "100%",
    height: "auto", // Changed from 100% to auto
    objectFit: "contain",
    zIndex: 0,
    position: "relative",
    aspectRatio: "2/1", // Corrected syntax and changed from 1/3 to 3/1
  },
  loaderOverlay: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    top: 0,
    color: "white",
    justifyContent: "center",
    alignItems: "center",
    display: "flex",
    zIndex: -100,
    opacity: 0.1,
  },
  dotsContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    gap: "5px",
    paddingTop: "10px",
  },
  dot: {
    width: "8px",
    height: "8px",
    borderRadius: "50%",
    cursor: "pointer",
  },
};
